import type { InstantAdminDatabase } from "@instantdb/admin";
import type { InstaQLEntity } from "@instantdb/core";
import { map, pipe } from "@uri/gamla";
import stringify from "safe-stable-stringify";
import {
  apiClient,
  getUploadUrl,
  storeTransferPayload,
} from "../../backend/src/api.ts";
import type schema from "../../instant.schema.ts";
import { chatPath } from "../../landing/src/paths.ts";
import {
  encryptAsymmetric,
  generateKeyPair,
  generateSymmetricKey,
} from "../../protocol/src/crypto.ts";
import { normalizeAlias } from "./alias.ts";
import { buildSignedRequest } from "./authClient.ts";
import {
  base64ToBase64Url,
  decryptAsymmetric,
  decryptBinary,
  decryptSymmetric,
  encryptBinary,
  type EncryptedAsymmetric,
  type EncryptedSymmetric,
  encryptSymmetric,
  sign,
  verify,
} from "./crypto.ts";

export const instantAppId = "8f3bebac-da7b-44ab-9cf5-46a6cc11557e";

type AttachmentBase = {
  url: string;
  name: string;
  size: number;
};

export type ImageAttachment = AttachmentBase & {
  type: "image";
  mimeType: `image/${string}`;
  width?: number;
  height?: number;
};

export type AudioAttachment = AttachmentBase & {
  type: "audio";
  mimeType: `audio/${string}`;
  duration?: number;
};

export type VideoAttachment = AttachmentBase & {
  type: "video";
  mimeType: `video/${string}`;
  duration?: number;
  width?: number;
  height?: number;
};

export type FileAttachment = AttachmentBase & {
  type: "file";
  mimeType: string;
};

export type LocationAttachment = {
  type: "location";
  latitude: number;
  longitude: number;
  label?: string;
};

export type Attachment =
  | ImageAttachment
  | AudioAttachment
  | VideoAttachment
  | FileAttachment
  | LocationAttachment;

import {
  assertTextLengthOk,
  fileSizeLimits,
  getFileSizeLimitByMimeType,
  maxTextLength,
  MB,
} from "./attachmentLimits.ts";
export { fileSizeLimits, getFileSizeLimitByMimeType, maxTextLength, MB };

type TextMessage = {
  type: "text";
  text: string;
  attachments?: Attachment[];
  elementId?: string;
  replyTo?: string;
};

type EditMessage = {
  type: "edit";
  editOf: string;
  text: string;
  attachments?: Attachment[];
};

type SpinnerMessage = {
  type: "spinner";
  text: string;
  active: boolean;
  elementId: string;
};

type ProgressMessage = {
  type: "progress";
  text: string;
  percentage: number;
  elementId: string;
};

type ReactionMessage = {
  type: "reaction";
  reactTo: string;
  emoji: string;
  remove?: boolean;
};

export type CallAction = "offer" | "answer" | "reject" | "end";

export type CallMessage = {
  type: "call";
  callId: string;
  action: CallAction;
  sdp?: string;
  duration?: number;
};

type InternalMessage =
  | TextMessage
  | EditMessage
  | SpinnerMessage
  | ProgressMessage
  | CallMessage
  | ReactionMessage;

export type Profile = {
  publicSignKey: string;
  name?: string;
  avatar?: string;
  alias?: string;
};

export type ConversationInfoPayload = {
  participants: Profile[]; // first N participants (capped)
  isPartial: boolean; // true if there are more participants not included
};

export type WebhookUpdate = {
  payload: EncryptedMessage;
  timestamp: number;
  conversationId: string;
  messageId: string;
};

type SignedPayload<T> = {
  publicSignKey: string;
  signature: string;
  payload: T;
};

export type EncryptedMessage = EncryptedSymmetric<
  SignedPayload<InternalMessage>
>;

const msgToStr = stringify;

export type EncryptedConversationKey = EncryptedAsymmetric<string>;

type SendMessageParams = {
  conversationKey: string;
  credentials: Credentials;
  message: InternalMessage;
  conversation: string;
};

export const sendMessage = async (params: {
  credentials: Credentials;
  conversation: string;
  message: InternalMessage;
}): Promise<{ messageId: string }> =>
  sendMessageWithKey({
    ...params,
    conversationKey: await getConversationKey(
      params.credentials,
      params.conversation,
    ),
  });

const encryptAndSign = async (
  conversationKey: string,
  { privateSignKey, publicSignKey }: Pick<
    Credentials,
    "privateSignKey" | "publicSignKey"
  >,
  message: InternalMessage,
) => {
  const serialized = msgToStr(message);
  assertTextLengthOk(serialized);
  return encryptSymmetric(conversationKey, {
    payload: message,
    publicSignKey,
    signature: await sign(privateSignKey, serialized),
  });
};

export const sendMessageWithKey = async ({
  conversationKey,
  conversation,
  credentials,
  message,
}: SendMessageParams): Promise<{ messageId: string }> =>
  apiClient({
    endpoint: "sendMessage",
    payload: {
      conversation,
      encryptedMessage: await encryptAndSign(
        conversationKey,
        credentials,
        message,
      ),
    },
  });

type DbMessage = InstaQLEntity<typeof schema, "messages">;

export const getConversationKey = async (
  creds: Credentials,
  convo: string,
): Promise<string> => {
  const result = await apiClient({
    endpoint: "conversationKey",
    payload: {
      conversationId: convo,
      publicSignKey: creds.publicSignKey,
    },
  });
  if ("error" in result) {
    throw new Error("No keys found for conversation");
  }
  return await decryptAsymmetric<string>(
    creds.privateEncryptKey,
    result.conversationKey as EncryptedAsymmetric<string>,
  );
};

export const handleWebhookUpdate = async (
  whUpdate: WebhookUpdate,
  credentials: Credentials,
): Promise<
  | {
    conversationId: string;
    message: DistributeOmit<DecipheredMessage, "id">;
    conversationKey: string;
    messageId: string;
  }
  | undefined
> => {
  const key = await getConversationKey(credentials, whUpdate.conversationId);
  const message = await decryptMessagePayload(key)(whUpdate);
  if (!message) return undefined;
  return {
    conversationId: whUpdate.conversationId,
    message,
    conversationKey: key,
    messageId: whUpdate.messageId,
  };
};

type DecipheredMessageBase = {
  id: string;
  publicSignKey: string;
  timestamp: number;
  text: string;
};

type DecipheredTextMessage = DecipheredMessageBase & {
  type: "text";
  attachments?: Attachment[];
  elementId?: string;
  replyTo?: string;
};

type DecipheredEditMessage = DecipheredMessageBase & {
  type: "edit";
  editOf: string;
  attachments?: Attachment[];
};

type DecipheredSpinnerMessage = DecipheredMessageBase & {
  type: "spinner";
  active: boolean;
  elementId: string;
};

type DecipheredProgressMessage = DecipheredMessageBase & {
  type: "progress";
  percentage: number;
  elementId: string;
};

type DecipheredCallMessage = DecipheredMessageBase & {
  type: "call";
  callId: string;
  action: CallAction;
  sdp?: string;
  duration?: number;
};

type DecipheredReactionMessage = DecipheredMessageBase & {
  type: "reaction";
  reactTo: string;
  emoji: string;
  remove?: boolean;
};

export type DecipheredMessage =
  | DecipheredTextMessage
  | DecipheredEditMessage
  | DecipheredSpinnerMessage
  | DecipheredProgressMessage
  | DecipheredCallMessage
  | DecipheredReactionMessage;

type DistributeOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K>
  : never;

const decryptedPayloadToMessage = (
  publicSignKey: string,
  timestamp: number,
  decryptedPayload: InternalMessage,
): DistributeOmit<DecipheredMessage, "id"> => {
  const base = {
    publicSignKey,
    timestamp,
    text: "text" in decryptedPayload ? decryptedPayload.text : "",
  };
  if (decryptedPayload.type === "call") {
    return {
      ...base,
      type: "call",
      callId: decryptedPayload.callId,
      action: decryptedPayload.action,
      sdp: decryptedPayload.sdp,
      duration: decryptedPayload.duration,
    };
  }
  if (decryptedPayload.type === "spinner") {
    return {
      ...base,
      type: "spinner",
      active: decryptedPayload.active,
      elementId: decryptedPayload.elementId,
    };
  }
  if (decryptedPayload.type === "progress") {
    return {
      ...base,
      type: "progress",
      percentage: decryptedPayload.percentage,
      elementId: decryptedPayload.elementId,
    };
  }
  if (decryptedPayload.type === "edit") {
    return {
      ...base,
      type: "edit",
      editOf: decryptedPayload.editOf,
      attachments: decryptedPayload.attachments,
    };
  }
  if (decryptedPayload.type === "reaction") {
    return {
      ...base,
      type: "reaction",
      reactTo: decryptedPayload.reactTo,
      emoji: decryptedPayload.emoji,
      remove: decryptedPayload.remove,
    };
  }
  return {
    ...base,
    type: "text",
    attachments: decryptedPayload.attachments,
    elementId: "elementId" in decryptedPayload
      ? decryptedPayload.elementId
      : undefined,
    replyTo: decryptedPayload.replyTo,
  };
};

const decryptMessagePayload = (conversationSymmetricKey: string) =>
async (
  { payload, timestamp }: Omit<DbMessage, "id">,
) => {
  try {
    const { signature, payload: decryptedPayload, publicSignKey } =
      await decryptSymmetric<SignedPayload<InternalMessage>>(
        conversationSymmetricKey,
        payload,
      );
    if (!(await verify(signature, publicSignKey, msgToStr(decryptedPayload)))) {
      return undefined;
    }
    return decryptedPayloadToMessage(
      publicSignKey,
      timestamp,
      decryptedPayload,
    );
  } catch {
    return undefined;
  }
};

export const decryptMessage = (conversationSymmetricKey: string) =>
async (
  { id, payload, timestamp }: DbMessage,
): Promise<DecipheredMessage | undefined> => {
  const base = await decryptMessagePayload(conversationSymmetricKey)({
    payload,
    timestamp,
  });
  if (!base) return undefined;
  return { id, ...base } as DecipheredMessage;
};

// deno-lint-ignore ban-types
type Identity = InstaQLEntity<typeof schema, "identities", { account: {} }>;

export const createConversation = (
  db: () => InstantAdminDatabase<typeof schema>,
) =>
async (
  publicSignKeys: string[],
  conversationTitle: string,
  credentials: Credentials,
) => {
  const { identities } = await db().query({
    identities: {
      account: {},
      $: { where: { publicSignKey: { $in: publicSignKeys } } },
    },
  });
  if (identities.length !== publicSignKeys.length) {
    return { error: "invalid-participants" };
  }
  const signKeyToEncrypionKey = pipe(
    map(({ publicSignKey, publicEncryptKey }: Identity) =>
      [publicSignKey, publicEncryptKey] as const
    ),
    Object.fromEntries<string>,
  )(identities);
  const symmetricKey = await generateSymmetricKey();
  return pipe(
    map(async (publicSignKey: string) =>
      [
        publicSignKey,
        await encryptAsymmetric(
          signKeyToEncrypionKey[publicSignKey],
          symmetricKey,
        ),
      ] as const
    ),
    Object.fromEntries<EncryptedConversationKey>,
    async (publicSignKeyToEncryptedSymmetricKey) =>
      apiClient({
        endpoint: "createConversation",
        payload: await buildSignedRequest(credentials, "createConversation", {
          publicSignKeyToEncryptedSymmetricKey,
          title: conversationTitle,
        }),
      }),
  )(publicSignKeys);
};

export type Credentials = {
  publicSignKey: string;
  privateSignKey: string;
  privateEncryptKey: string;
};

export const createIdentity = async (
  name: string,
  alias?: string,
): Promise<Credentials> => {
  const { publicKey, privateKey } = await generateKeyPair("sign");
  const encryptKey = await generateKeyPair("encrypt");
  await apiClient({
    endpoint: "createAnonymousIdentity",
    payload: {
      name,
      publicSignKey: publicKey,
      publicEncryptKey: encryptKey.publicKey,
      ...(alias ? { alias } : {}),
    },
  });
  return {
    publicSignKey: publicKey,
    privateSignKey: privateKey,
    privateEncryptKey: encryptKey.privateKey,
  };
};

export const baseUrl = "https://aliceandbot.com";

export const chatWithMeLink = (
  publicSignKey: string,
  topic?: string,
): string => {
  const url = `${baseUrl}${chatPath}?chatWith=${
    encodeURIComponent(publicSignKey)
  }`;
  return topic ? `${url}&topic=${encodeURIComponent(topic)}` : url;
};

export const generateTransferUrl = async (
  creds: Credentials,
  chatWithPubKey?: string,
): Promise<string> => {
  const aesKey = await generateSymmetricKey();
  const encrypted = await encryptSymmetric(aesKey, creds);
  const { relayId } = await storeTransferPayload(encrypted);
  const fragment = `transfer=${relayId}:${base64ToBase64Url(aesKey)}`;
  const url = `${baseUrl}${chatPath}`;
  return chatWithPubKey
    ? `${url}?chatWith=${encodeURIComponent(chatWithPubKey)}#${fragment}`
    : `${url}#${fragment}`;
};

export const setAlias = async ({
  alias,
  credentials,
}: { alias: string; credentials: Credentials }): Promise<
  | { success: true }
  | {
    success: false;
    error: "alias-taken" | "invalid-alias" | "not-found" | "invalid-auth";
  }
> =>
  apiClient({
    endpoint: "setAlias",
    payload: await buildSignedRequest(credentials, "setAlias", {
      alias: normalizeAlias(alias),
    }),
  });

export const setName = async ({
  name,
  credentials,
}: { name: string; credentials: Credentials }): Promise<
  | { success: true }
  | {
    success: false;
    error: "not-found" | "invalid-auth" | "invalid-name";
  }
> =>
  apiClient({
    endpoint: "setName",
    payload: await buildSignedRequest(credentials, "setName", {
      name: name.trim(),
    }),
  });

export const setAvatar = async ({
  avatar,
  credentials,
}: { avatar?: string; credentials: Credentials }): Promise<
  | { success: true }
  | {
    success: false;
    error: "not-found" | "invalid-auth" | "invalid-avatar";
  }
> =>
  apiClient({
    endpoint: "setAvatar",
    payload: await buildSignedRequest(credentials, "setAvatar", {
      avatar: avatar?.trim() || undefined,
    }),
  });

export const renameConversation = async ({
  conversationId,
  title,
  credentials,
}: {
  conversationId: string;
  title: string;
  credentials: Credentials;
}): Promise<
  | { success: true }
  | {
    success: false;
    error: "not-found" | "invalid-auth" | "not-admin" | "invalid-title";
  }
> =>
  apiClient({
    endpoint: "renameConversation",
    payload: await buildSignedRequest(credentials, "renameConversation", {
      conversationId,
      title,
    }),
  });

export const publicSignKeyToAlias = (
  publicSignKey: string,
): Promise<{ alias: string } | { error: "no-such-identity" | "no-alias" }> =>
  apiClient({
    endpoint: "publicSignKeyToAlias",
    payload: { publicSignKey },
  });

const arrayBufferToHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const computeHash = async (data: ArrayBuffer): Promise<string> => {
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return arrayBufferToHex(hashBuffer);
};

const getAudioDuration = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    if (
      !file.type.startsWith("audio/") ||
      typeof globalThis.window === "undefined" ||
      // @ts-ignore: Audio is a DOM API not available in backend
      typeof Audio === "undefined"
    ) {
      resolve(undefined);
      return;
    }
    // @ts-ignore: Audio is a DOM API not available in backend
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const dur = audio.duration;
      URL.revokeObjectURL(audio.src);
      if (!isFinite(dur) || isNaN(dur)) {
        resolve(undefined);
        return;
      }
      resolve(Math.round(dur));
    };
    audio.onerror = () => resolve(undefined);
    audio.src = URL.createObjectURL(file);
  });

export const uploadAttachment = async ({
  credentials,
  conversationId,
  conversationKey,
  file,
  durationOverride,
}: {
  credentials: Credentials;
  conversationId: string;
  conversationKey: string;
  file: File;
  durationOverride?: number;
}): Promise<Attachment | { error: string }> => {
  const limit = getFileSizeLimitByMimeType(file.type);
  if (file.size > limit) {
    return { error: `file-too-large:${Math.round(limit / MB)}MB` };
  }
  const arrayBuffer = await file.arrayBuffer();
  const encrypted = await encryptBinary(conversationKey, arrayBuffer);
  const contentHash = await computeHash(encrypted);

  const result = await getUploadUrl(credentials, {
    conversationId,
    contentHash,
    fileName: file.name,
    contentType: "application/octet-stream",
  });
  if ("error" in result) return { error: result.error };

  const { uploadUrl, fileUrl, maxSize } = result;

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/octet-stream",
      "x-goog-content-length-range": `0,${maxSize}`,
    },
    body: encrypted,
  });
  if (!response.ok) {
    return {
      error: `upload-failed:${response.status}:${await response.text()}`,
    };
  }

  const base: { url: string; name: string; size: number } = {
    url: fileUrl,
    name: file.name,
    size: file.size,
  };

  if (file.type.startsWith("image/")) {
    return { ...base, type: "image", mimeType: file.type as `image/${string}` };
  }
  if (file.type.startsWith("audio/")) {
    const duration = durationOverride ?? await getAudioDuration(file);
    return {
      ...base,
      type: "audio",
      mimeType: file.type as `audio/${string}`,
      duration,
    };
  }
  if (file.type.startsWith("video/")) {
    return { ...base, type: "video", mimeType: file.type as `video/${string}` };
  }
  return { ...base, type: "file", mimeType: file.type };
};

export const downloadAttachment = async ({
  url,
  conversationKey,
}: {
  url: string;
  conversationKey: string;
}): Promise<ArrayBuffer> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${await response.text()}`,
    );
  }
  const encrypted = await response.arrayBuffer();
  return decryptBinary(conversationKey, encrypted);
};

import { serverBaseUrl } from "./serverUrl.ts";

export const uiUpdateUrl: string = `${serverBaseUrl}/ui-update`;

export const buildUiUpdateUrl = (elementId: string): string =>
  `${uiUpdateUrl}?elementId=${encodeURIComponent(elementId)}`;

export const setPriceTagSigned = async ({
  priceTag,
  credentials,
}: {
  priceTag: number;
  credentials: Credentials;
}) =>
  apiClient({
    endpoint: "setPriceTag",
    payload: await buildSignedRequest(credentials, "setPriceTag", {
      priceTag,
    }),
  });

export const getBalanceAndTransactionsSigned = async (
  credentials: Credentials,
) =>
  apiClient({
    endpoint: "getBalanceAndTransactions",
    payload: await buildSignedRequest(
      credentials,
      "getBalanceAndTransactions",
      {},
    ),
  });

export const prepareCryptoPaymentSigned = async (
  params: { amount: number; credentials: Credentials },
) =>
  apiClient({
    endpoint: "prepareCryptoPayment",
    payload: await buildSignedRequest(
      params.credentials,
      "prepareCryptoPayment",
      { amount: params.amount },
    ),
  });

export const checkCryptoPaymentSigned = async (
  params: { paymentAddress: string; credentials: Credentials },
) =>
  apiClient({
    endpoint: "checkCryptoPayment",
    payload: await buildSignedRequest(
      params.credentials,
      "checkCryptoPayment",
      { paymentAddress: params.paymentAddress },
    ),
  });

export const getProfile = (publicSignKey: string) =>
  apiClient({
    endpoint: "getProfile",
    payload: { publicSignKey },
  });
